import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  FaArrowLeft,
  FaCalendarAlt,
  FaChevronLeft,
  FaChevronRight,
  FaExternalLinkAlt,
  FaImages,
  FaSearchPlus,
  FaTimes,
} from 'react-icons/fa';
import './NewsPage.css';
import Header from '../Header/Header.js';
import Footer from '../Footer/Footer.js';
import PageNotFound from '../PageNotFound/PageNotFound.js';
import NewsLoader from '../NewsLoader/NewsLoader.js';
import { getApiBaseUrl } from '../../utils/api.js';
import { getNewsImageUrl, getNewsYear } from '../../utils/news.js';

const gallerySwipeMinDistance = 40;

function NewsPage({ onBack }) {
  const { id } = useParams();
  const [newsItem, setNewsItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedGalleryImageIndex, setSelectedGalleryImageIndex] = useState(null);
  const galleryTouchStartRef = useRef({ x: 0, y: 0 });
  const newsImages = newsItem?.images || [];
  const selectedGalleryImage = selectedGalleryImageIndex === null
    ? null
    : newsImages[selectedGalleryImageIndex];

  useEffect(() => {
    fetch(`${getApiBaseUrl()}/api/news/${id}`)
      .then((res) => {
        if (!res.ok) throw new Error('Ошибка загрузки новости');
        return res.json();
      })
      .then((data) => setNewsItem(data))
      .catch((err) => console.error(err))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    setSelectedGalleryImageIndex(null);
  }, [id]);

  const showPreviousGalleryImage = useCallback(() => {
    setSelectedGalleryImageIndex((currentIndex) => {
      if (currentIndex === null || newsImages.length === 0) {
        return currentIndex;
      }

      return (currentIndex - 1 + newsImages.length) % newsImages.length;
    });
  }, [newsImages.length]);

  const showNextGalleryImage = useCallback(() => {
    setSelectedGalleryImageIndex((currentIndex) => {
      if (currentIndex === null || newsImages.length === 0) {
        return currentIndex;
      }

      return (currentIndex + 1) % newsImages.length;
    });
  }, [newsImages.length]);

  useEffect(() => {
    if (selectedGalleryImageIndex === null) {
      document.body.style.overflow = '';
      return undefined;
    }

    document.body.style.overflow = 'hidden';

    function handleKeyDown(evt) {
      if (evt.key === 'Escape') {
        setSelectedGalleryImageIndex(null);
      }

      if (evt.key === 'ArrowLeft' && newsImages.length > 1) {
        showPreviousGalleryImage();
      }

      if (evt.key === 'ArrowRight' && newsImages.length > 1) {
        showNextGalleryImage();
      }
    }

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [
    selectedGalleryImageIndex,
    newsImages.length,
    showNextGalleryImage,
    showPreviousGalleryImage,
  ]);

  function handleGalleryTouchStart(evt) {
    const touch = evt.changedTouches[0];

    galleryTouchStartRef.current = {
      x: touch.clientX,
      y: touch.clientY,
    };
  }

  function handleGalleryTouchEnd(evt) {
    if (newsImages.length < 2) {
      return;
    }

    const touch = evt.changedTouches[0];
    const swipeDistanceX = touch.clientX - galleryTouchStartRef.current.x;
    const swipeDistanceY = touch.clientY - galleryTouchStartRef.current.y;

    if (
      Math.abs(swipeDistanceX) < gallerySwipeMinDistance
      || Math.abs(swipeDistanceX) <= Math.abs(swipeDistanceY)
    ) {
      return;
    }

    if (swipeDistanceX > 0) {
      showPreviousGalleryImage();
      return;
    }

    showNextGalleryImage();
  }

  if (loading)
    return (
      <>
        <Header />
        <main className="content">
          <section className="newsPage newsPage--loading">
            <NewsLoader text="Загрузка новости..." />
          </section>
        </main>
        <Footer />
      </>
    );

  if (!newsItem)
    return (
      <PageNotFound
        onBack={onBack}
        statusError="404"
        textError="Новость не найдена"
      />
    );

  const newsYear = getNewsYear(newsItem);

  return (
    <>
      <Header />
      <main className="content">
        <section className="newsPage">
          <section className="newsPage__hero">
            <div className="newsPage__hero-content">
              <p className="newsPage__eyebrow">Новости ВДПО</p>
              <h2 className="newsPage__title">{newsItem.title}</h2>
              <p className="newsPage__lead">
                Публикация о событиях, мероприятиях и инициативах Самарского
                областного отделения ВДПО.
              </p>
            </div>

            <aside className="newsPage__hero-side">
              <div className="newsPage__meta-card">
                <div className="newsPage__meta-item">
                  <FaCalendarAlt className="newsPage__meta-icon" />
                  <div>
                    <p className="newsPage__meta-label">Дата публикации</p>
                    <p className="newsPage__meta-value">{newsItem.date}</p>
                  </div>
                </div>
                {newsYear ? (
                  <div className="newsPage__meta-item">
                    <FaCalendarAlt className="newsPage__meta-icon" />
                    <div>
                      <p className="newsPage__meta-label">Год</p>
                      <p className="newsPage__meta-value">{newsYear}</p>
                    </div>
                  </div>
                ) : null}
                <div className="newsPage__meta-item">
                  <FaImages className="newsPage__meta-icon" />
                  <div>
                    <p className="newsPage__meta-label">Фотоматериалы</p>
                    <p className="newsPage__meta-value">
                      {newsImages.length > 0
                        ? `${newsImages.length} изображений`
                        : 'Без фотогалереи'}
                    </p>
                  </div>
                </div>
                {newsItem.link ? (
                  <a
                    href={newsItem.link}
                    className="newsPage__meta-link"
                    target="_blank"
                    rel="noreferrer"
                  >
                    <FaExternalLinkAlt />
                    Перейти к источнику
                  </a>
                ) : null}
              </div>
            </aside>
          </section>

          <article className="newsPage__article">
            {newsItem.text.map((paragraph, index) => (
              <p className="newsPage__text" key={index}>
                {paragraph}
              </p>
            ))}
          </article>

          {newsItem.link ? (
            <section className="newsPage__source">
              <p className="newsPage__section-kicker">Ссылка</p>
              <h3 className="newsPage__section-title">Источник публикации</h3>
              <a
                href={newsItem.link}
                className="newsPage__link"
                target="_blank"
                rel="noreferrer"
              >
                {newsItem.link}
              </a>
            </section>
          ) : null}

          {newsImages.length > 0 ? (
            <section className="newsPage__gallery-section">
              <p className="newsPage__section-kicker">Фотогалерея</p>
              <h3 className="newsPage__section-title">
                Фотографии к материалу
              </h3>
              <div className="newsPage__image-container">
                {newsImages.map((img, index) => (
                  <figure className="newsPage__image-card" key={img + index}>
                    <button
                      type="button"
                      className="newsPage__image-button"
                      onClick={() => setSelectedGalleryImageIndex(index)}
                      aria-label={`Открыть изображение ${index + 1}`}
                    >
                      <img
                        className="newsPage__image"
                        src={getNewsImageUrl(img)}
                        alt={`${newsItem.title} - изображение ${index + 1}`}
                      />
                      <span className="newsPage__image-zoom" aria-hidden="true">
                        <FaSearchPlus />
                      </span>
                    </button>
                  </figure>
                ))}
              </div>
            </section>
          ) : null}

          <div className="newsPage__button-back-container">
            <button
              type="button"
              onClick={onBack}
              className="newsPage__button-back"
            >
              <FaArrowLeft /> Назад
            </button>
          </div>
        </section>
      </main>
      {selectedGalleryImage ? (
        <div
          className="newsPage__lightbox"
          onClick={() => setSelectedGalleryImageIndex(null)}
          role="presentation"
        >
          <div
            className="newsPage__lightbox-content"
            onClick={(evt) => evt.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label={`Изображение ${selectedGalleryImageIndex + 1} из ${newsImages.length}`}
          >
            <p className="newsPage__lightbox-counter">
              {selectedGalleryImageIndex + 1} / {newsImages.length}
            </p>
            {newsImages.length > 1 ? (
              <button
                type="button"
                className="newsPage__lightbox-nav newsPage__lightbox-nav_prev"
                onClick={showPreviousGalleryImage}
                aria-label="Предыдущее изображение"
              >
                <FaChevronLeft />
              </button>
            ) : null}
            <button
              type="button"
              className="newsPage__lightbox-close"
              onClick={() => setSelectedGalleryImageIndex(null)}
              aria-label="Закрыть изображение"
            >
              <FaTimes />
            </button>
            <img
              src={getNewsImageUrl(selectedGalleryImage)}
              alt={`${newsItem.title} - изображение ${selectedGalleryImageIndex + 1}`}
              className="newsPage__lightbox-image"
              onTouchStart={handleGalleryTouchStart}
              onTouchEnd={handleGalleryTouchEnd}
            />
            {newsImages.length > 1 ? (
              <button
                type="button"
                className="newsPage__lightbox-nav newsPage__lightbox-nav_next"
                onClick={showNextGalleryImage}
                aria-label="Следующее изображение"
              >
                <FaChevronRight />
              </button>
            ) : null}
          </div>
        </div>
      ) : null}
      <Footer />
    </>
  );
}

export default NewsPage;
